import { fetchApi, postApi, ACCOUNTS_API } from '@/api/client'
import type {
  Account,
  CreateAccountRequest,
  SetupMongoDBRequest,
  SetupWarehouseRequest,
  StarrocksSetupRequest,
  StarrocksOnboardRequest,
  StarrocksExportRequest,
  SetupDefaultsResponse,
  WarehouseJobResponse,
  SetupUnificationRequest,
  UniConfigResponse,
} from '../types'

export async function listAccounts(): Promise<Account[]> {
  return fetchApi<Account[]>(ACCOUNTS_API)
}

export async function getAccount(accId: number): Promise<Account> {
  return fetchApi<Account>(`${ACCOUNTS_API}/${accId}`)
}

export async function createAccount(data: CreateAccountRequest): Promise<Account> {
  return postApi<Account>(ACCOUNTS_API, data)
}

export async function getSetupDefaults(): Promise<SetupDefaultsResponse> {
  return fetchApi<SetupDefaultsResponse>(`${ACCOUNTS_API}/setup/defaults`)
}

export async function setupMongoDB(accId: number, data: SetupMongoDBRequest): Promise<void> {
  return postApi<void>(`${ACCOUNTS_API}/${accId}/setup/mongodb`, data)
}

/** Start an async warehouse setup job. Returns immediately with jobId + status=RUNNING. */
export async function startWarehouseSetup(
  accId: number,
  data: SetupWarehouseRequest
): Promise<WarehouseJobResponse> {
  return postApi<WarehouseJobResponse>(`${ACCOUNTS_API}/${accId}/setup/warehouse`, data)
}

export async function setupUnification(accId: number, data: SetupUnificationRequest): Promise<void> {
  return postApi<void>(`${ACCOUNTS_API}/${accId}/setup/unification`, data)
}

/** Return uni config (field names + entity mappings) for the account. */
export async function getUniConfig(accId: number): Promise<UniConfigResponse> {
  return fetchApi<UniConfigResponse>(`${ACCOUNTS_API}/${accId}/setup/unification`)
}

/** Poll the status of a running warehouse setup job. */
export async function getWarehouseJobStatus(
  accId: number,
  jobId: string
): Promise<WarehouseJobResponse> {
  return fetchApi<WarehouseJobResponse>(
    `${ACCOUNTS_API}/${accId}/setup/warehouse/status/${jobId}`
  )
}

/** Start an async StarRocks account onboarding job. Returns immediately with jobId + status=RUNNING. */
export async function startStarrocksSetup(
  accId: number,
  data: StarrocksSetupRequest
): Promise<WarehouseJobResponse> {
  return postApi<WarehouseJobResponse>(`${ACCOUNTS_API}/${accId}/setup/starrocks`, data)
}

/** Poll the status of a running StarRocks setup job. */
export async function getStarrocksJobStatus(
  accId: number,
  jobId: string
): Promise<WarehouseJobResponse> {
  return fetchApi<WarehouseJobResponse>(
    `${ACCOUNTS_API}/${accId}/setup/starrocks/status/${jobId}`
  )
}

/** Start async StarRocks onboard (schema only — no data). */
export async function startStarrocksOnboard(
  accId: number,
  data: StarrocksOnboardRequest
): Promise<WarehouseJobResponse> {
  return postApi<WarehouseJobResponse>(`${ACCOUNTS_API}/${accId}/setup/starrocks`, data)
}

/** Start async StarRocks backfill: export from BQ/SF to GCS then import into SR. */
export async function startStarrocksExport(
  accId: number,
  data: StarrocksExportRequest
): Promise<WarehouseJobResponse> {
  return postApi<WarehouseJobResponse>(`${ACCOUNTS_API}/${accId}/setup/starrocks/export`, data)
}
